import axiosInstance from '../lib/axios'
import { API_ENDPOINTS } from '../constants/api'
import type { Order, OrderListResponse, OrderStatus } from '../types/api.types'

export interface OrderListParams {
  status?: OrderStatus
  tableNumber?: number
  unprintedOnly?: boolean
  limit?: number
}

export const ordersService = {
  // Newest first, line items included. One query for the whole page, so this is
  // safe to poll.
  async list(propertyId: number, params: OrderListParams = {}): Promise<Order[]> {
    const query = new URLSearchParams({ property_id: String(propertyId) })
    if (params.status) query.set('status', params.status)
    if (params.tableNumber != null) query.set('table_number', String(params.tableNumber))
    if (params.unprintedOnly) query.set('unprinted_only', 'true')
    query.set('limit', String(params.limit ?? 100))

    const { data } = await axiosInstance.get<OrderListResponse>(`${API_ENDPOINTS.ORDERS}?${query}`)
    return data.orders ?? []
  },

  // One order by id, for callers holding an order_id and nothing else — the
  // conversation drawer, which would otherwise fetch a hundred to find one.
  async getById(id: number): Promise<Order> {
    const { data } = await axiosInstance.get<Order>(API_ENDPOINTS.ORDER_BY_ID(id))
    return data
  },

  // Status is the only editable field. Repeating the current status is a no-op
  // server-side, so callers don't have to guard against a double tap.
  async setStatus(id: number, status: OrderStatus): Promise<Order> {
    const { data } = await axiosInstance.patch<Order>(API_ENDPOINTS.ORDER_BY_ID(id), { status })
    return data
  },

  // DELETE cancels — the row survives with status CANCELLED, and the response
  // carries the final state so there's no follow-up read.
  async cancel(id: number): Promise<Order> {
    const { data } = await axiosInstance.delete<Order>(API_ENDPOINTS.ORDER_BY_ID(id))
    return data
  },
}
